import { useCallback, useEffect, useState } from "react";
import { Send, Undo2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge, Money } from "@/components/common";
import ReasonDialog from "@/components/ReasonDialog";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const POS_LABEL = { payable: "Net VAT payable", refundable: "Net VAT refundable", nil: "Nil" };

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-right" data-testid={testid}>{v}</span></div>
);

export const ShareDialog = ({ open, onOpenChange, res, periodLabel, onShared }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date().toISOString());
  const [prior, setPrior] = useState([]);
  useEffect(() => {
    if (!open || !res) { return; }
    setNow(new Date().toISOString());
    api.get("/vat-reports", { params: { client_id: res.client.id } }).then(({ data }) => setPrior(data.filter((r) => r.date_from === res.date_from && r.date_to === res.date_to && r.status !== "withdrawn"))).catch(() => setPrior([]));
  }, [open, res]);
  if (!res) return null;
  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vat-reports/share", { client_id: res.client.id, date_from: res.date_from, date_to: res.date_to, period_label: periodLabel || "" });
      toast.success(`${data.report_no} shared with client`);
      if (data.superseded.length) { toast.info(`Earlier pending ${data.superseded.join(", ")} replaced`); }
      if (!data.portal_enabled) { toast.warning("This client's portal is not enabled yet — enable it in Client Manager so they can view the report"); }
      onShared(); onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const s = res.summary;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="vat-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="w-4 h-4 text-emerald-700" />Share VAT report with client</DialogTitle>
          <DialogDescription>Please confirm the details. A fixed copy of these figures will appear in the client's portal under VAT Reports.</DialogDescription>
        </DialogHeader>
        <div className="rounded border border-slate-200 px-3">
          <Row k="Company name" v={`${res.client.company_name} · ${res.client.code}`} testid="vat-share-company" />
          <Row k="VAT consolidation period" v={<span className="num">{fmtDate(res.date_from)} – {fmtDate(res.date_to)}{periodLabel ? ` (${periodLabel})` : ""}</span>} testid="vat-share-period" />
          <Row k="Consolidated by" v={user.name} testid="vat-share-by" />
          <Row k="Consolidated date & time" v={<span className="num">{fmtDateTime(now)}</span>} testid="vat-share-at" />
          <Row k={POS_LABEL[s.position]} v={<span className="num">{fmtMoney(Math.abs(s.net_vat))} SAR</span>} testid="vat-share-net" />
        </div>
        {prior.map((r) => (
          <div key={r.id} className={`text-xs rounded border px-3 py-2 ${r.status === "pending" ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="vat-share-prior">
            {r.status === "pending" ? `${r.report_no} is already shared for this period and still pending — it will be replaced by this new report.` : `${r.report_no} for this period was already marked "${r.status.replace("_", " ")}" by the client. Sharing again creates a new Pending report.`}
          </div>
        ))}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vat-share-cancel">Cancel</Button>
          <Button disabled={busy} onClick={share} className="bg-emerald-800 hover:bg-emerald-900" data-testid="vat-share-confirm"><Send className="w-4 h-4 mr-1.5" />{busy ? "Sharing…" : "Confirm & share"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SharedReports = ({ clientId, tick }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [wd, setWd] = useState(null);
  const load = useCallback(() => {
    api.get("/vat-reports", { params: { client_id: clientId } }).then(({ data }) => {
      setRows(data);
      if (user.role !== "auditor" && data.some((r) => r.staff_seen === false)) {
        setTimeout(() => document.querySelector('[data-testid="vat-shared-reports"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        api.post("/vat-reports/seen", null, { params: { client_id: clientId } }).then(() => window.dispatchEvent(new Event("ksavat-notif-refresh"))).catch(() => {});
      }
    }).catch(() => {});
  }, [clientId, user.role]);
  useEffect(() => { if (clientId) { load(); } }, [clientId, tick, load]);
  if (!rows.length) return null;
  const withdraw = async (reason) => {
    try { await api.post(`/vat-reports/${wd.id}/withdraw`, { reason }); toast.success("Report withdrawn"); setWd(null); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="panel overflow-hidden fade-up" data-testid="vat-shared-reports">
      <div className="px-4 py-3 border-b border-slate-200 font-display font-semibold">Shared with client <span className="num text-xs text-slate-400 ml-1">{rows.length}</span></div>
      <div className="overflow-x-auto max-h-[360px]">
        <table className="w-full">
          <thead className="sticky top-0"><tr>{["Report", "Period", "Net VAT", "Shared by", "Status", "Client remarks", ""].map((h) => <th key={h} className={`th ${h === "Net VAT" ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id} className="row-hover" data-testid={`vat-shared-row-${i}`}>
              <td className="td num text-xs">{r.report_no}</td>
              <td className="td num text-xs">{fmtDate(r.date_from)} – {fmtDate(r.date_to)}</td>
              <td className="td text-right"><Money value={Math.abs(r.summary.net_vat)} strong /><div className="text-[10px] text-slate-400">{POS_LABEL[r.position]}</div></td>
              <td className="td text-xs">{r.shared_by_name}<div className="num text-[10px] text-slate-400">{fmtDateTime(r.shared_at)}</div></td>
              <td className="td"><StatusBadge status={r.status} testid={`vat-shared-status-${i}`} />{r.staff_seen === false && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-400 text-[#064E3B] text-[10px] font-bold" data-testid={`vat-shared-new-${i}`}>NEW</span>}{r.status_at && <div className="num text-[10px] text-slate-400 mt-0.5">{fmtDateTime(r.status_at)}</div>}</td>
              <td className="td text-xs max-w-[260px]">
                {r.status === "withdrawn" ? <span className="text-slate-400">{r.withdrawn_reason || "Withdrawn"} · {r.withdrawn_by_name}</span> : r.remarks}
                {r.challan.length > 0 && <div className="flex gap-1 mt-1">{r.challan.map((im) => <a key={im.thumb_path} href={im.hd_url} target="_blank" rel="noreferrer"><img src={im.thumb_url} alt="challan" className="w-10 h-10 rounded object-cover border" /></a>)}</div>}
              </td>
              <td className="td text-right">{user.role === "super_admin" && r.status !== "withdrawn" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setWd(r)} data-testid={`vat-shared-withdraw-${i}`}><Undo2 className="w-4 h-4 mr-1" />Withdraw</Button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <ReasonDialog open={!!wd} onOpenChange={(o) => !o && setWd(null)} title={`Withdraw ${wd?.report_no || ""}`} description="The report will disappear from the client's portal. The record is kept here." confirmLabel="Withdraw" destructive testid="vat-withdraw-dialog" onConfirm={withdraw} />
    </div>
  );
};
